/**
 * SavingsCard — first per-month savings estimate.
 *
 * Heyflow widget tag:
 *   <div data-conny-widget="savings-card"></div>
 *
 * Required Heyflow fields (any of these aliases is accepted; matching is by
 * suffix so `information.apartment_rent_amount` etc. also works):
 *   1. Monthly rent (€)        — apartment_rent_amount / current_rent / rent /
 *                                monthly_rent
 *      Accepts German ("1.450,00") or US ("1,450.00") formatted numbers,
 *      with or without "€".
 *   2. Living area (m²)        — apartment_living_area / living_area /
 *                                apartment_size / area / sqm
 *   3. ZIP code                — Address (zip) field, a 5-digit ZIP inside a
 *                                "Full address" field, or a standalone zip /
 *                                postal_code / plz variable.
 *
 * Reads from sessionStorage:
 *   STORAGE_KEYS.rent, .area, .zip, .address
 *
 * If any of the three inputs is missing the card shows a "we still need …"
 * placeholder instead of a number.
 */
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { getZip } from "../../shared/api";
import { animateNumber, formatDecimal, formatEuro } from "../../shared/numbers";
import { parseLocalizedNumber } from "../../shared/number-parse";
import { normalizeZip } from "../../shared/address";
import { STORAGE_KEYS, useStorage } from "../../shared/storage";

interface Computed {
  zip: string;
  rent: number | null;
  area: number | null;
  achievedRentPerSqm: number | null;
  baseline: number | null;
  lower: number | null;
  upper: number | null;
}

function useComputed(): Computed {
  const [rentRaw] = useStorage(STORAGE_KEYS.rent);
  const [areaRaw] = useStorage(STORAGE_KEYS.area);
  const [zipRaw] = useStorage(STORAGE_KEYS.zip);
  const [addressRaw] = useStorage(STORAGE_KEYS.address);

  const zip = normalizeZip(zipRaw || addressRaw);
  const rent = parseLocalizedNumber(rentRaw);
  const area = parseLocalizedNumber(areaRaw);

  const { data: zipStats } = useQuery({
    queryKey: ["zip", zip],
    queryFn: () => getZip(zip),
    enabled: Boolean(zip),
    staleTime: 30 * 60_000,
  });

  const achieved = zipStats?.achieved_rent_per_sqm ?? null;
  const baseline =
    rent != null && area != null && achieved != null
      ? rent - achieved * area
      : null;

  let lower = baseline != null ? baseline * 0.9 : null;
  let upper = baseline != null ? baseline * 1.382 : null;

  if (lower != null && upper != null && rent != null && area != null) {
    if (lower < 0) lower = 0;
    if (upper > rent) upper = rent;
    const maxPct = rent * 0.631;
    if (upper > maxPct) upper = maxPct;
    if (area > 0) {
      const minReduced = 6.11 * area;
      const maxSqm = rent - minReduced;
      if (upper > maxSqm) upper = maxSqm;
    }
    upper = Math.max(0, upper);
    if (lower > upper) lower = upper;
  }

  return {
    zip,
    rent,
    area,
    achievedRentPerSqm: achieved,
    baseline,
    lower,
    upper,
  };
}

export function SavingsCard() {
  const c = useComputed();

  const hasData =
    c.rent != null &&
    c.area != null &&
    c.achievedRentPerSqm != null &&
    c.baseline != null;
  const tooLow = hasData && (c.upper ?? 0) < 50;

  const showRange = hasData && !tooLow;

  return (
    <div className="cn">
      <div className="cn-card cn-fade-in">
        <span className="cn-eyebrow">Your first savings estimate</span>

        {!hasData && (
          <>
            <div className="cn-unclear">
              Looks like your rent may already be close
            </div>
            <div className="cn-source">
              We need your rent, apartment size and address before we can show a
              first estimate.
            </div>
          </>
        )}

        {hasData && tooLow && (
          <>
            <div className="cn-unclear">
              Looks like your rent may already be close
            </div>
            <div className="cn-source">
              It seems you may not be overpaying by a lot. We need to take a
              closer look at your contract and apartment details.
            </div>
          </>
        )}

        {showRange && (
          <>
            <div className="cn-range">
              <div className="cn-range-track">
                <div className="cn-range-line" />
                <div className="cn-range-dot cn-range-dot--left" />
                <div className="cn-range-dot cn-range-dot--right" />
              </div>
              <div className="cn-range-endpoints">
                <div>
                  <strong>
                    <EuroCounter value={Math.round(c.lower!)} />
                  </strong>
                  <span>lower estimate / month</span>
                </div>
                <div>
                  <strong>
                    <EuroCounter value={Math.round(c.upper!)} />
                  </strong>
                  <span>higher potential / month</span>
                </div>
              </div>
            </div>
            <div className="cn-source">
              Based on achieved square-meter rents in ZIP code {c.zip} (€
              {formatDecimal(c.achievedRentPerSqm!)}/m²).
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function EuroCounter({ value }: { value: number }) {
  const [n, setN] = useState(0);
  useEffect(() => animateNumber(value, setN), [value]);
  return <>{formatEuro(n)}</>;
}
